import React from 'react';

import { useTranslation } from '@lyearn/i18n';
import { FolderOutline } from '@lyearn/icons';
import { TypographyWithTooltip } from '@lyearn/molecules';
import { cn } from '@lyearn/style';
import { Stack, Typography } from '@lyearn/ui';
import VirtualizedList from '@/components/VirtualizedList';
import SearchListEmptyState from '@/modules/corporate/components/SearchListEmptyState';
import {
  FILE_SEARCH_PROJECT_VARIABLES,
  useFileSearch,
} from '@/modules/tasks/features/actionItems/hooks/useFileSearch';
import { getFileIcon } from '@/modules/tasks/utils/getFileIcon';

interface ProjectsListProps {
  search: string;
  setSearch: (search: string) => void;
  selectedFileId: string | null;
  selectItem: (fileId: string | null) => void;
}

function ProjectsList({ search, setSearch, selectedFileId, selectItem }: ProjectsListProps) {
  const { t } = useTranslation();

  const {
    files: projects,
    isFetching: isFetchingProjects,
    fetchMore,
    totalCount,
  } = useFileSearch({ search, filters: FILE_SEARCH_PROJECT_VARIABLES });

  const projectItemRenderer = (index: number, project: NonNullable<typeof projects>[number]) => (
    <Stack
      key={project.id}
      alignItems="center"
      className={cn('border-divider ps-16 pe-20 py-10 hover:bg-bg-secondary cursor-pointer', {
        'border-t': index > 0,
        '!bg-bg-hover': selectedFileId === project.id,
      })}
      direction="row"
      gap={8}
      justifyContent="between"
      role="button"
      onClick={() => selectItem(project.id)}>
      {getFileIcon(project, 22, 'text-icon-primary shrink-0')}
      <TypographyWithTooltip
        className="flex-1 truncate"
        color="text-text-paragraph"
        numberOfLines={1}
        variant="body-short-01">
        {project.name}
      </TypographyWithTooltip>
    </Stack>
  );

  return (
    <Stack className="w-full" direction="col" gapY={24}>
      <Stack alignItems="center" className="w-full" direction="row" justifyContent="between">
        <Typography className="ms-8" variant="label-01">
          {t('Task:DuplicateTask.AllProjects')}
        </Typography>
        <Typography className="me-8" color="text-text-placeholder" variant="label-02">
          {t('Task:Task_plural', { humanReadableCount: totalCount })}
        </Typography>
      </Stack>

      {!totalCount ? (
        <SearchListEmptyState
          Icon={FolderOutline}
          className="!my-20"
          clearSearch={() => setSearch('')}
          search={search}
          title={t('Task:MoveToProjectDialog.EmptyState')}
        />
      ) : (
        <Stack className="w-full h-[25rem]">
          <VirtualizedList
            className="flex-1 border border-border rounded-12 overflow-clip"
            data={projects}
            endReached={fetchMore}
            itemContent={projectItemRenderer}
            totalCount={totalCount}
          />
        </Stack>
      )}
    </Stack>
  );
}

export default ProjectsList;
